import React, {
    useState,
    useEffect,
    useRef,
    useCallback
} from 'react';


import RIQButton from '../../../../components/RIQButton.jsx';
import {
    RIQPopover
} from '../../../../components/RIQPopover.jsx';
import DefineQuery from './DefineQuery.jsx';
import ChooseSource from './ChooseSource.jsx';
import NoiseSuppression from './NoiseSuppression.jsx';

import WizardScrollingPanel from './WizardScrollingPanel.jsx';
import useSaveAsSavedSearchModal from '../../hooks/useSaveAsSavedSearchModal.jsx';

import {
    useLoadLandscapeListeningQuery,
    useSaveLandscapeListeningQuery,
    useLoadSampleListeningQuery,
    useListAccountSavedSearches
} from '../../hooks/useListeningQueryPersistence.js';

import {
    useCurationConfigState,
    useCurationConfigValue,
    useSetCurationConfigState,
    useSerializeConfigurations,
    useUpdateProps
} from '../../../../hooks/widgetConfiguration.js';

import {
    useVerifySearch,
    ValidationStates
} from '../../hooks/useVerifySearch.js';

import {
    listeningDefinitions
} from '../enums/listeningDefinitions.jsx';

const {
    saveTopic,
    saveAsTopic,
    saveAsNewTopic,
    outOfTopics,
    topicsUnsupported,
    updateTopic,
    updateSampleTopicTooltip
} = listeningDefinitions['create-saved-search-button'];

function useUpdateSearch(onSettled) {
    const [editingListeningQueryId, setEditingListeningQueryId] = useCurationConfigState('editingListeningQueryId');

    const {mutate: saveCurationConfiguration} = useSaveLandscapeListeningQuery(editingListeningQueryId);
    return useSerializeConfigurations(allProps => {
        saveCurationConfiguration({
            name: allProps.name,
            definition: _.omit(allProps, 'name', 'editingListeningQueryId')
        }, {
            onSettled: (data, error) => {
                if (data) {
                    setEditingListeningQueryId(data.id);
                }
                onSettled(data, error);
            }
        });
    });
}


function SavedSearchConfig({setIsSourceInvalid}) {
    return (
        <WizardScrollingPanel
            className='tlw-flex tlw-flex-col tlw-gap-5 tlw-rounded-b tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-bg-gray-150 tlw-p-7.5'
        >

            <DefineQuery
            />

            <ChooseSource
                setIsSourceInvalid={setIsSourceInvalid}
            />

            <NoiseSuppression
            />
        </WizardScrollingPanel>
    );
}


function QueueingButton({label, variant, disabled, tooltipText, onClick}) {
    const {validationState} = useVerifySearch(true);

    const validationStateOnMouseDown = useRef(null);
    const [shouldApplyOnValid, setShouldApplyOnValid] = useState(false);

    useEffect(() => {
        if (shouldApplyOnValid) {
            if (validationState === ValidationStates.Valid) {
                onClick();
            } else if (validationState !== ValidationStates.Validating) {
                setShouldApplyOnValid(false);
            }
        }
    }, [validationState, shouldApplyOnValid, onClick]);

    function onApplyDown() {
        validationStateOnMouseDown.current = validationState;
    }

    function onApply() {
        if (disabled) {
            return;
        }

        if (validationState === ValidationStates.Valid) {
            onClick();
            setShouldApplyOnValid(false);
        } else if (validationState === ValidationStates.Validating && validationStateOnMouseDown.current !== ValidationStates.Validating) {
            // need to queue the submit iff the search is valid.
            setShouldApplyOnValid(true);
        }
    }

    const btnElement = (
        // This div is to grab the click event in case the user clicks on the button as the action they do to remove focus from the input.
        // See #35409
        <div
            className='tlw-self-center'
            onClick={onApply}
            onMouseDown={onApplyDown}
            aria-hidden='true'
        >
            <RIQButton
                variant={variant}
                className='tlw-self-center tlw-px-6 tlw-py-3'
                // we don't call a function here because onApply is called on the div above instead.
                onClick={null}
                disabled={validationState !== ValidationStates.Valid || disabled}
            >
                {label}
            </RIQButton>
        </div>
    );

    if (tooltipText) {
        return (
            <RIQPopover
                triggerNode={btnElement}
                placement='top'
                variant={disabled ? 'tooltip' : 'default'}
            >
                {tooltipText}
            </RIQPopover>
        );
    } else {

        return btnElement;
    }
}

function SavedSearchActions({isSourceInvalid, isEditingInstantSearch, isNewInstantSearch}) {

    const [isSaving, setIsSaving] = useState(false);
    const editingListeningQueryId = useCurationConfigValue('editingListeningQueryId');
    const searchTerms = useCurationConfigValue('searchTerms');
    const landscapeId = useCurationConfigValue('landscapeId');
    const {validationState} = useVerifySearch(!_.isEmpty(searchTerms), {
        searchTerms: searchTerms
    });
    const isInvalidSavedSearch = validationState === ValidationStates.Invalid || isSourceInvalid;

    const {data: sampleTopic} = useLoadSampleListeningQuery(editingListeningQueryId);
    const isSampleTopic = Boolean(sampleTopic && landscapeId !== sampleTopic?.landscapeId);

    const plan = useCurationConfigValue('plan');
    const numSavedSearchesAllowed = plan?.max_listening_saved_searches;
    const {data: allSavedSearches} = useListAccountSavedSearches();
    const canCreateMoreSearches = Boolean(numSavedSearchesAllowed && allSavedSearches && allSavedSearches.length < numSavedSearchesAllowed);

    const setShowAdvancedSearchEditor = useSetCurationConfigState('showAdvancedSearchEditor');

    const propsSetter = useUpdateProps();

    const onApplyClick = useCallback(() => {
        setShowAdvancedSearchEditor(false);
    }, [setShowAdvancedSearchEditor]);

    const postSaveCallback = useCallback((data, error) => {
        setIsSaving(false);

        if (error) {
            RivalIQ.execute('alert:danger', 'An error occurred, please try again');
        } else {
            propsSetter({
                searchTerms: null,
                language: null,
                source: null,
                searchTermsAnd: null,
                searchTermsNot: null,
                excludeAuthor: null,
                theme: null,
                domain: null,
                excludeDomain: null
            });

            const params = {
                listeningQueryId: data.id
            };

            if (editingListeningQueryId !== data.id) {
                params.interstitial = true;
            }

            RivalIQ.execute('history:navigate', 'listening/search', {
                params: params,
                trigger: true
            });
        }
    }, [editingListeningQueryId, propsSetter, setIsSaving]);

    const updateSavedSearch = useUpdateSearch(postSaveCallback);

    function getSaveAsNewTooltipText() {
        const canSaveNew = (numSavedSearchesAllowed > 0);
        if (!canSaveNew) {
            return topicsUnsupported;
        }

        if (_.size(allSavedSearches) < numSavedSearchesAllowed) {
            return null;
        }

        return outOfTopics(numSavedSearchesAllowed);
    }

    function onUpdateSavedSearchClick() {
        if (!isInvalidSavedSearch) {
            setIsSaving(true);
            updateSavedSearch();
        }
    }

    const {showSaveAsSavedSearchModal, SaveAsSavedSearchModal} = useSaveAsSavedSearchModal(setIsSaving, postSaveCallback, isSaving);

    return (
        <>
            {!isEditingInstantSearch && !editingListeningQueryId &&
                <QueueingButton
                    label={saveTopic}
                    variant='primary'
                    disabled={isSourceInvalid}
                    onClick={showSaveAsSavedSearchModal}
                />
            }

            {!isEditingInstantSearch && editingListeningQueryId &&
            <>
                <QueueingButton
                    label={saveAsNewTopic}
                    variant='default'
                    disabled={isSourceInvalid || !canCreateMoreSearches}
                    onClick={showSaveAsSavedSearchModal}
                    tooltipText={getSaveAsNewTooltipText()}
                />

                <QueueingButton
                    label={updateTopic}
                    variant='primary'
                    disabled={isSourceInvalid || isSaving || isSampleTopic}
                    onClick={onUpdateSavedSearchClick}
                    tooltipText={isSampleTopic ? updateSampleTopicTooltip : null}
                />
            </>
            }

            {isEditingInstantSearch && !editingListeningQueryId &&
            <>
                <QueueingButton
                    label={saveAsTopic}
                    variant='default'
                    disabled={isSourceInvalid || !canCreateMoreSearches}
                    onClick={showSaveAsSavedSearchModal}
                    tooltipText={getSaveAsNewTooltipText()}
                />

                <QueueingButton
                    label={isNewInstantSearch ? 'View Results' : 'Apply Changes'}
                    variant='primary'
                    disabled={isSourceInvalid}
                    onClick={onApplyClick}
                />
            </>
            }

            {SaveAsSavedSearchModal}
        </>
    );
}


export default function SavedSearchBuilder({setIsSourceInvalid, isSourceInvalid, isEditingInstantSearch, isNewInstantSearch}) {

    const editingListeningQueryId = useCurationConfigValue('editingListeningQueryId');
    const {data: savedSearch} = useLoadLandscapeListeningQuery(editingListeningQueryId);

    return (
        <div
            className='tlw-flex tlw-w-full tlw-flex-col tlw-rounded tlw-bg-white tlw-shadow-panel lg_tlw-absolute'
        >
            <div
                className='tlw-m-0 tlw-flex tlw-grow tlw-flex-row tlw-gap-5 tlw-p-7.5 tlw-text-base tlw-leading-22 tlw-text-gray-900'
            >
                {!isEditingInstantSearch && !editingListeningQueryId &&
                <div
                    className='tlw-flex tlw-grow tlw-items-center tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap'
                >
                    <h4 className='tlw-m-0 tlw-text-lg tlw-leading-base tlw-text-gray-900'>Search Settings</h4>
                </div>
                }

                {!isEditingInstantSearch && editingListeningQueryId &&
                    <div
                        className='tlw-m-0 tlw-grow tlw-self-center tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap tlw-text-lg tlw-text-gray-900'
                    >
                        {savedSearch?.name}
                    </div>
                }

                {isEditingInstantSearch &&
                    <h4 className='tlw-m-0 tlw-grow tlw-self-center tlw-text-lg tlw-text-gray-900 '>Instant Search Settings</h4>
                }

                <SavedSearchActions
                    isEditingInstantSearch={isEditingInstantSearch}
                    isSourceInvalid={isSourceInvalid}
                    isNewInstantSearch={isNewInstantSearch}
                />

            </div>

            <SavedSearchConfig
                setIsSourceInvalid={setIsSourceInvalid}
                isEditingInstantSearch={isEditingInstantSearch}
            />
        </div>
    );
}
